import React from 'react';

export interface FilterState {
  dow?: number;      // 0=Sun,1=Mon,...,5=Fri; undefined = all
  gapDir?: 'up' | 'down' | 'flat';
  openPos?: 'inside' | 'above' | 'below';
  gapMin?: number;
  gapMax?: number;
  tf?: 'm5' | 'm15' | 'm30' | 'h1';
  stopPct?: 100 | 75 | 50 | 25;
}

// NOTE: tf and stopPct are handled by ORBFilterPanel, not this component.

interface Props {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  sessionCount?: number;
}

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const TF_LABELS = ['M5', 'M15', 'M30', 'H1'];
const STOP_LABELS = ['100%', '75%', '50%', '25%'];
const STOP_VALS: FilterState['stopPct'][] = [100, 75, 50, 25];

function activeCount(f: FilterState): number {
  let n = 0;
  if (f.dow !== undefined) n++;
  if (f.gapDir) n++;
  if (f.openPos) n++;
  if (f.gapMin !== undefined || f.gapMax !== undefined) n++;
  return n;
}

export default function FilterPanel({ filters, onChange, sessionCount }: Props) {
  const count = activeCount(filters);

  const set = (patch: Partial<FilterState>) => onChange({ ...filters, ...patch });
  const clear = () => onChange({});

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.title}>Filters</span>
        {count > 0 && (
          <span style={styles.badge}>{count} active</span>
        )}
        {sessionCount !== undefined && (
          <span style={styles.count}>{sessionCount.toLocaleString()} sessions</span>
        )}
        {count > 0 && (
          <button style={styles.clearBtn} onClick={clear}>Clear</button>
        )}
      </div>

      <div style={styles.row}>
        {/* Day of Week */}
        <div style={styles.group}>
          <div style={styles.groupLabel}>Day of Week</div>
          <div style={styles.btnGroup}>
            <button
              style={{ ...styles.toggleBtn, ...(filters.dow === undefined ? styles.toggleBtnActive : {}) }}
              onClick={() => set({ dow: undefined })}
            >All</button>
            {[1, 2, 3, 4, 5].map(d => (
              <button
                key={d}
                style={{ ...styles.toggleBtn, ...(filters.dow === d ? styles.toggleBtnActive : {}) }}
                onClick={() => set({ dow: filters.dow === d ? undefined : d })}
              >{DOW_LABELS[d]}</button>
            ))}
          </div>
        </div>

        {/* Gap Direction */}
        <div style={styles.group}>
          <div style={styles.groupLabel}>Gap Direction</div>
          <select
            style={styles.select}
            value={filters.gapDir ?? ''}
            onChange={e => set({ gapDir: e.target.value as FilterState['gapDir'] || undefined })}
          >
            <option value="">All</option>
            <option value="up">Gap Up</option>
            <option value="down">Gap Down</option>
            <option value="flat">Flat</option>
          </select>
        </div>

        {/* Open Position */}
        <div style={styles.group}>
          <div style={styles.groupLabel}>Open Position</div>
          <select
            style={styles.select}
            value={filters.openPos ?? ''}
            onChange={e => set({ openPos: e.target.value as FilterState['openPos'] || undefined })}
          >
            <option value="">All</option>
            <option value="inside">Inside</option>
            <option value="above">Above</option>
            <option value="below">Below</option>
          </select>
        </div>

        {/* Gap Size */}
        <div style={styles.group}>
          <div style={styles.groupLabel}>Gap Size (pts)</div>
          <div style={styles.rangeRow}>
            <input
              style={styles.numInput}
              type="number"
              placeholder="Min"
              value={filters.gapMin ?? ''}
              onChange={e => set({ gapMin: e.target.value ? parseFloat(e.target.value) : undefined })}
            />
            <span style={styles.rangeSep}>–</span>
            <input
              style={styles.numInput}
              type="number"
              placeholder="Max"
              value={filters.gapMax ?? ''}
              onChange={e => set({ gapMax: e.target.value ? parseFloat(e.target.value) : undefined })}
            />
          </div>
        </div>


      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    background: '#1e293b',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 12,
    borderTop: '3px solid #3b82f6',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    color: '#e2e8f0',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  badge: {
    background: '#3b82f6',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 10,
  },
  count: {
    fontSize: 12,
    color: '#64748b',
    marginLeft: 'auto',
  },
  clearBtn: {
    background: '#334155',
    color: '#94a3b8',
    border: 'none',
    borderRadius: 6,
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    alignItems: 'flex-end',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  groupLabel: {
    fontSize: 11,
    color: '#475569',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  btnGroup: {
    display: 'flex',
    gap: 2,
  },
  toggleBtn: {
    background: '#334155',
    color: '#94a3b8',
    border: 'none',
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  toggleBtnActive: {
    background: '#3b82f6',
    color: '#fff',
  },
  select: {
    background: '#334155',
    color: '#e2e8f0',
    border: 'none',
    borderRadius: 4,
    padding: '5px 8px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    outline: 'none',
  },
  rangeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  rangeSep: {
    color: '#475569',
    fontSize: 12,
  },
  numInput: {
    background: '#334155',
    color: '#e2e8f0',
    border: 'none',
    borderRadius: 4,
    padding: '5px 8px',
    fontSize: 12,
    width: 70,
    outline: 'none',
  },
};